import {Controller} from "@hotwired/stimulus"
import {startIcons} from "../import_lucid.js"

export default class extends Controller {
    static values = {
        organizationSlug: String,
        entitySearch: { type: Boolean, default: false }
    }

    static targets = ["modalBg", "modal", "search", "results"]
    items = []

    connect() {
        if (!this.boundKeydown) {
            this.boundKeydown = this.handleKeydown.bind(this)
            window.addEventListener("keydown", this.boundKeydown)
        }
        this.activeIndex = -1
        this.suppressMouseHover = false
        this.boundMouseMove = () => {
            if (this.suppressMouseHover) {
                this.suppressMouseHover = false
            }
        }
        window.addEventListener("mousemove", this.boundMouseMove)
    }

    disconnect() {
        if (this.boundKeydown) {
            window.removeEventListener("keydown", this.boundKeydown)
            this.boundKeydown = null
        }
        window.removeEventListener("mousemove", this.boundMouseMove)
        // A debounced entity search outliving a Turbo navigation fires a fetch nobody reads
        clearTimeout(this._entitySearchTimer)
    }

    // Close only when the click lands on the padding around the panel, not inside it
    closeOnBackdrop(event) {
        if (event.target === this.modalTarget) this.closeCommandPalette()
    }

    closeCommandPalette() {
        this.modalBgTarget.classList.add("hidden")
        this.modalTarget.classList.add("hidden")
        this.searchTarget.value = ""
        this.resultsTarget.innerHTML = ""
        let quickLinks = this.element.querySelector(`[data-id='quick-links']`)
        quickLinks?.classList.remove("hidden")
    }

    openCommandPalette() {
        this.modalTarget.classList.remove("hidden")
        this.modalBgTarget.classList.remove("hidden")
        this.searchTarget.focus()
        this.highlightFirst()
    }

    openSearchPalette(event) {
        event.stopPropagation()
        this.modalTarget.classList.remove("hidden")
        this.modalBgTarget.classList.remove("hidden")
        this.searchTarget.focus()
        this.highlightFirst()
        this.fetchItems()
    }

    // Select the first navigable row so ArrowUp/Down and Enter work before any typing
    highlightFirst() {
        this.activeIndex = -1
        this.moveSelection(1)
    }

    handleKeydown(event) {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
            event.preventDefault()
            this.openCommandPalette()
            this.fetchItems()
        } else if (event.key === "Escape" && !this.modalTarget.classList.contains("hidden")) {
            event.preventDefault()
            this.closeCommandPalette()
        }

        if (!this.modalTarget.classList.contains("hidden")) {
            const emptyQuery = this.searchTarget.value === ""
            switch (event.key) {
                case "Enter":
                    event.preventDefault()
                    this.selectActiveItem(event.metaKey || event.ctrlKey)
                    this.closeCommandPalette()
                    break
                case "ArrowDown":
                    event.preventDefault()
                    this.moveByDirection(0, 1)
                    break
                case "ArrowUp":
                    event.preventDefault()
                    this.moveByDirection(0, -1)
                    break
                case "ArrowRight":
                    if (!emptyQuery) break // let the caret move while typing
                    event.preventDefault()
                    this.moveByDirection(1, 0)
                    break
                case "ArrowLeft":
                    if (!emptyQuery) break
                    event.preventDefault()
                    this.moveByDirection(-1, 0)
                    break
            }
        }
    }

    // Navigable rows: search results if present, otherwise the visible quick-links
    navItems() {
        const results = this.resultsTarget.querySelectorAll("[data-item]")
        if (results.length) return results
        const quickLinks = this.element.querySelector(`[data-id='quick-links']`)
        if (quickLinks && !quickLinks.classList.contains("hidden")) {
            return quickLinks.querySelectorAll("a")
        }
        return results
    }

    moveSelection(direction) {
        const items = this.navItems()
        if (items.length === 0) return
        this.suppressMouseHover = true
        this.clearActiveClasses()

        this.activeIndex += direction
        if (this.activeIndex < 0) this.activeIndex = items.length - 1
        if (this.activeIndex >= items.length) this.activeIndex = 0

        const activeItem = items[this.activeIndex]
        activeItem.classList.add("lz-cmdk-active")
        activeItem.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }

    // Geometric 2D navigation so arrows work in the grid layout (varying column
    // counts) as well as vertical lists. dx/dy = direction (-1, 0, 1).
    moveByDirection(dx, dy) {
        const items = Array.from(this.navItems())
        if (items.length === 0) return

        const currentIdx = (this.activeIndex >= 0 && this.activeIndex < items.length) ? this.activeIndex : 0
        const cr = items[currentIdx].getBoundingClientRect()
        const cx = cr.left + cr.width / 2
        const cy = cr.top + cr.height / 2

        let best = -1, bestScore = Infinity
        items.forEach((el, i) => {
            if (i === currentIdx) return
            const r = el.getBoundingClientRect()
            const ddx = (r.left + r.width / 2) - cx
            const ddy = (r.top + r.height / 2) - cy
            // Candidate must lie in the pressed direction (small epsilon to ignore same-line jitter).
            if (dx === 1 && ddx <= 1) return
            if (dx === -1 && ddx >= -1) return
            if (dy === 1 && ddy <= 1) return
            if (dy === -1 && ddy >= -1) return
            // Prefer small movement along the axis, penalise cross-axis drift.
            const primary = dx !== 0 ? Math.abs(ddx) : Math.abs(ddy)
            const cross = dx !== 0 ? Math.abs(ddy) : Math.abs(ddx)
            const score = primary + cross * 3
            if (score < bestScore) { bestScore = score; best = i }
        })
        if (best === -1) return // at an edge in that direction

        this.suppressMouseHover = true
        this.clearActiveClasses()
        this.activeIndex = best
        items[best].classList.add("lz-cmdk-active")
        items[best].scrollIntoView({ behavior: "smooth", block: "nearest" })
    }

    selectActiveItem(openInNewTab = false) {
        const items = this.navItems()
        const activeItem = items[this.activeIndex]
        if (activeItem) {
            const link = activeItem.tagName === "A" ? activeItem : activeItem.querySelector("a")
            if (link) {
                if (openInNewTab) {
                    window.open(link.href, '_blank')
                } else {
                    link.click()
                }
            }
        }
    }

    async fetchItems() {
        try {
            const slug = this.organizationSlugValue
            const response = await fetch(`/${slug}/command_palette`)
            this.items = await response.json()
            this.filterItems()
        } catch (error) {
            console.error("Error fetching command palette items:", error)
        }
    }

    groupBySection(items) {
        return items.reduce((acc, item) => {
            (acc[item.section] ||= []).push(item)
            return acc
        }, {})
    }

    filterItems() {
        let query = this.searchTarget.value.toLowerCase().trim()
        let quickLinks = this.element.querySelector(`[data-id='quick-links']`)
        if (!query) {
            this.resultsTarget.innerHTML = ""
            quickLinks?.classList.remove("hidden")
            this.highlightFirst()
            return
        }
        quickLinks?.classList.add("hidden")
        let filtered = query
            ? this.items.filter(item =>
                item.name.toLowerCase().includes(query.toLowerCase())
            )
            : this.items

        this.resultsTarget.innerHTML = ""
        this.activeIndex = -1

        const grouped = this.groupBySection(filtered)
        this.renderGroupedItems(grouped)

        // Removed if the live entity search below returns records
        if (filtered.length === 0) {
            this.resultsTarget.innerHTML = `<div data-no-results class="flex flex-col items-center justify-center gap-2 py-16 text-gray-400">
                <i data-lucide="search-x" class="w-6 h-6"></i>
                <p class="text-sm">No results for “${this.escapeHtml(query)}”</p>
            </div>`
            startIcons(this.resultsTarget)
        }

        // Live entity search for queries 3+ chars (permission-gated)
        if (query.length >= 3 && this.entitySearchValue) {
            this.searchEntities(query)
        }
    }

    async searchEntities(query) {
        if (this._entitySearchTimer) clearTimeout(this._entitySearchTimer)
        this._entitySearchTimer = setTimeout(async () => {
            // Show loader
            const existing = this.resultsTarget.querySelector("[data-entity-search]")
            if (existing) existing.remove()

            const loader = document.createElement("div")
            loader.dataset.entitySearch = "true"
            loader.innerHTML = `<div class="px-5 sm:px-6 pt-3 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-[0.08em]">Records</div>
                <div class="flex items-center gap-2 px-5 sm:px-6 py-3 text-sm text-gray-400">
                    <span class="lz-spinner-icon h-4 w-4"></span> Searching records...
                </div>`
            this.resultsTarget.appendChild(loader)

            try {
                const slug = this.organizationSlugValue
                const response = await fetch(`/${slug}/entities/search?q=${encodeURIComponent(query)}`)
                if (!response.ok) throw new Error(`HTTP ${response.status}`)
                const results = await response.json()

                // Remove loader
                const loaderEl = this.resultsTarget.querySelector("[data-entity-search]")
                if (loaderEl) loaderEl.remove()

                if (results.length === 0) return

                const container = document.createElement("div")
                container.dataset.entitySearch = "true"

                this.resultsTarget.querySelector("[data-no-results]")?.remove()

                const header = document.createElement("div")
                header.className = "px-5 sm:px-6 pt-3 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-[0.08em]"
                header.textContent = "Records"
                container.appendChild(header)

                results.forEach(result => {
                    if (result.report_links && result.report_links.length > 0) {
                        // Show report links for this entity
                        result.report_links.forEach(link => {
                            const reportRow = this.createResultRow(
                                link.url,
                                `${result.display} — ${link.label}`,
                                result.table_name,
                                "bar-chart",
                                "bg-indigo-500/10",
                                "text-indigo-600"
                            )
                            container.appendChild(reportRow)
                        })
                    } else {
                        // No report links configured — show entity as non-clickable info
                        const infoRow = document.createElement("div")
                        infoRow.className = "flex items-center gap-3 px-3 py-2.5 mx-2 sm:mx-3 text-sm text-gray-400"
                        infoRow.innerHTML = `<span class="flex-shrink-0 w-9 h-9 rounded-[10px] bg-black/[0.04] ring-1 ring-inset ring-black/[0.05] flex items-center justify-center"><i data-lucide="database" class="w-[18px] h-[18px] text-gray-400"></i></span>
                            <div><div class="text-[15px] font-medium text-gray-500">${this.escapeHtml(result.display)}</div><div class="text-xs text-gray-400">${this.escapeHtml(result.table_name)} — no report linked</div></div>`
                        container.appendChild(infoRow)
                    }
                })

                this.resultsTarget.appendChild(container)
                startIcons(container)
            } catch (error) {
                // Remove loader on error
                const loaderEl = this.resultsTarget.querySelector("[data-entity-search]")
                if (loaderEl) loaderEl.remove()
            }
        }, 300)
    }

    createResultRow(href, name, subtitle, icon, iconBg, iconColor) {
        const row = document.createElement("div")
        row.dataset.item = "true"
        row.className = "flex items-center gap-3 px-3 py-2.5 mx-2 sm:mx-3 rounded-xl cursor-pointer transition-colors"

        row.innerHTML = `<div class="w-9 h-9 flex items-center justify-center ${iconBg} rounded-[10px] ring-1 ring-inset ring-black/[0.05] flex-shrink-0">
                <i data-lucide="${this.escapeHtml(icon)}" class="w-[18px] h-[18px] ${iconColor}"></i>
            </div>
            <div class="flex-1 min-w-0">
                <a href="${this.escapeHtml(href)}" class="block text-[15px] text-gray-800 truncate">${this.escapeHtml(name)}</a>
                ${subtitle ? `<span class="text-xs text-gray-400">${this.escapeHtml(subtitle)}</span>` : ''}
            </div>`

        row.addEventListener("mouseenter", () => {
            if (this.suppressMouseHover) return
            this.clearActiveClasses()
            row.classList.add("lz-cmdk-active")
            this.activeIndex = Array.from(this.resultsTarget.querySelectorAll("[data-item]")).indexOf(row)
        })

        row.addEventListener("mouseleave", () => {
            row.classList.remove("lz-cmdk-active")
        })

        return row
    }

    renderGroupedItems(grouped) {
        Object.entries(grouped).forEach(([section, items]) => {
            const header = document.createElement("div")
            header.className = "px-5 sm:px-6 pt-3 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-[0.08em]"
            header.textContent = section
            this.resultsTarget.appendChild(header)

            items.forEach((item) => {
                const row = this.createResultRow(
                    item.path,
                    item.name,
                    null,
                    item.icon,
                    'bg-indigo-500/10',
                    'text-indigo-500'
                )

                // Override with lucide icons and colour tints for report items
                const colour = this.escapeHtml(item.colour || "#6366f1")
                const iconHtml = `<div class="w-9 h-9 flex items-center justify-center rounded-[10px] ring-1 ring-inset ring-black/[0.06] flex-shrink-0" style="background: linear-gradient(180deg, ${colour}26, ${colour}12);">
                    <i data-lucide="${this.escapeHtml(item.icon || 'file-text')}" class="w-[18px] h-[18px]" style="color: ${colour};"></i>
                  </div>`

                row.innerHTML = `${iconHtml}
                      <div class="flex-1 min-w-0">
                          <a href="${this.escapeHtml(item.path)}" class="block text-[15px] font-medium text-gray-900 truncate">${this.escapeHtml(item.name)}</a></div>`

                this.resultsTarget.appendChild(row)
            })
        })

        startIcons(this.resultsTarget)

        // Auto-select first item
        const firstItem = this.resultsTarget.querySelector("[data-item]")
        if (firstItem) {
            this.activeIndex = 0
            firstItem.classList.add("lz-cmdk-active")
        }
    }

    clearActiveClasses() {
        this.resultsTarget
            .querySelectorAll("[data-item]")
            .forEach((el) => el.classList.remove("lz-cmdk-active"))
        this.element
            .querySelector(`[data-id='quick-links']`)
            ?.querySelectorAll("a")
            .forEach((el) => el.classList.remove("lz-cmdk-active"))
    }

    escapeHtml(str) {
        const div = document.createElement("div")
        div.textContent = str
        return div.innerHTML.replace(/"/g, '&quot;')
    }
}
